import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  inject,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { ConfirmationService, MessageService } from 'primeng/api';
import { Observable, Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { PermissaoService } from '@core/services/permissao.service';
import { Grupo } from '@admin/feactures-modules/permissoes/permissao.model';
import { Utilizador } from '@core/models/models';

/**
 * Não existe endpoint que liste os grupos a que um utilizador já pertence
 * (confirmado exaustivamente no Swagger — ver FASE 0 do módulo de Permissões).
 * Por isso esta tela não mostra "grupos actuais" — é uma ferramenta de acção
 * pura (adicionar/remover), nunca uma exibição de estado que o front não
 * consegue confirmar.
 */
@Component({
  selector: 'app-gerir-grupos-utilizador',
  standalone: true,
  imports: [CommonModule, FormsModule, DialogModule, SelectModule, ButtonModule, SkeletonModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './gerir-grupos-utilizador.component.html',
  styleUrl: './gerir-grupos-utilizador.component.scss',
})
export class GerirGruposUtilizadorComponent implements OnChanges, OnDestroy {
  @Input() visible = false;
  @Input() utilizador: Utilizador | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();

  private readonly permissaoSvc = inject(PermissaoService);
  private readonly msg = inject(MessageService);
  private readonly confirmSvc = inject(ConfirmationService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  grupos: Grupo[] = [];
  carregandoGrupos = false;
  erroGrupos = false;

  grupoSelecionadoId: string | null = null;
  processando = false;
  acaoProcessando: 'adicionar' | 'remover' | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible'] && this.visible) {
      this.grupoSelecionadoId = null;
      this.carregarGrupos();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private carregarGrupos(): void {
    this.carregandoGrupos = true;
    this.erroGrupos = false;
    this.cdr.markForCheck();

    this.permissaoSvc
      .listarGrupos({ pageNumber: 1, pageSize: 100 })
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.carregandoGrupos = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (res) => {
          this.grupos = res.data || [];
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error('Erro ao carregar grupos:', err);
          this.erroGrupos = true;
          this.cdr.markForCheck();
        },
      });
  }

  private get grupoSelecionado(): Grupo | undefined {
    return this.grupos.find((g) => g.id === this.grupoSelecionadoId);
  }

  adicionar(): void {
    if (!this.utilizador?.id || !this.grupoSelecionadoId || this.processando) return;

    this.acaoProcessando = 'adicionar';

    this.executar(
      this.permissaoSvc.adicionarUtilizadorAoGrupo(
        this.utilizador.id,
        this.grupoSelecionadoId,
      ),
      `${this.utilizador.nome} foi adicionado ao grupo "${this.grupoSelecionado?.nome}".`,
      'Não foi possível adicionar o utilizador ao grupo.',
    );
  }

  confirmarRemover(): void {
    if (!this.utilizador?.id || !this.grupoSelecionadoId || this.processando) return;
    const nomeGrupo = this.grupoSelecionado?.nome ?? '';

    this.confirmSvc.confirm({
      header: 'Remover do grupo',
      message: `Vais remover ${this.utilizador.nome} do grupo "${nomeGrupo}". Continuar?`,
      acceptLabel: 'Sim, remover',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.acaoProcessando = 'remover';
        this.executar(
          this.permissaoSvc.removerUtilizadorDoGrupo(
            this.utilizador!.id!,
            this.grupoSelecionadoId!,
          ),
          `${this.utilizador!.nome} foi removido do grupo "${nomeGrupo}".`,
          'Não foi possível remover o utilizador do grupo.',
        );
      },
    });
  }

  private executar(
    acao: Observable<any>,
    mensagemSucesso: string,
    mensagemErro: string,
  ): void {
    this.processando = true;
    this.cdr.markForCheck();

    acao
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => {
          this.processando = false;
          this.acaoProcessando = null;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: () => {
          this.msg.add({
            severity: 'success',
            summary: 'Sucesso',
            detail: mensagemSucesso,
            life: 3500,
          });
          this.grupoSelecionadoId = null;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error(mensagemErro, err);
        },
      });
  }

  fechar(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }
}
